import { styled } from '@mui/material/styles';
import { useGetTravelCompensationType } from 'queries';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Arrangement } from '../../types';

import Box from '@mui/material/Box';

import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';

import { convertArrangementTypeToOption } from '../../arrangementsUtils';
import {
  CodeExternField,
  CodeField,
  CompensationTypeIdFieldInner,
  DescriptionField,
  IsExternalField,
  TransportationModeField
} from '../FormFields';
import TravelLinesTable from './TravelLinesTable';
import { TransportationModeEnum } from 'RestAPIs/travel-distances/types';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';
import { DefaultReadModeComponent } from 'components/shared/CRUD/components/InnerPage/components/withReadModeFieldRenderers';

const PREFIX = 'ArrangementFormFields';

const classes = {
  root: `${PREFIX}-root`
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.root}`]: {
    flexDirection: 'column',
    flex: 1
  }
}));

interface Props {
  closeInnerView: () => void;
  rowData?: Arrangement;
}

const CodeFieldWithReadMode = withReadModeField(CodeField);
const CodeExternFieldWithReadMode = withReadModeField(CodeExternField);
const DescriptionFieldWithReadMode = withReadModeField(DescriptionField);
const CompensationTypeIdFieldWithReadMode = withReadModeField(
  CompensationTypeIdFieldInner
);
const TransportationModeFieldWithReadMode = withReadModeField(
  TransportationModeField
);

const renderCompensationTypeIdReadMode = ({
  name,
  label,
  arrangementTypes
}) => {
  const { getValues } = useFormContext();

  const selectedArrangementType = arrangementTypes?.find(
    (arrangementType) => arrangementType.id === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={name}
      label={label}
      value={selectedArrangementType?.name}
    />
  );
};

const renderTransportationModeFieldReadMode = ({
  name,
  transportationMode
}) => {
  const { t } = useTranslation('common');
  const { getValues } = useFormContext();

  const selectedTransportationMode = transportationMode?.find(
    (mode) => mode === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={t(`travelDistances.transportationModes.${name}`)}
      label={t('travelDistances.transportationMode')}
      value={
        selectedTransportationMode
          ? t(
              `travelDistances.transportationModes.${selectedTransportationMode}`
            )
          : '-'
      }
    />
  );
};

export const ArrangementFormFields: React.FC<Props> = ({
  closeInnerView,
  rowData
}) => {
  const { control } = useFormContext();
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const { data: arrangementTypes } = useGetTravelCompensationType();

  return (
    <StyledBox display="flex" className={classes.root}>
      <Box mb={2.5} sx={{ width: '100%' }}>
        <GridContainer mb={2.5}>
          <GridCompact6>
            <CodeFieldWithReadMode
              name="code"
              label={t('adminPortal.arrangements.code')}
              control={control}
            />
          </GridCompact6>

          <GridCompact6>
            <CodeExternFieldWithReadMode
              name="codeExtern"
              label={t('adminPortal.arrangements.externalCode')}
              control={control}
            />
          </GridCompact6>

          <GridCompact6>
            <DescriptionFieldWithReadMode
              name="description"
              label={t('adminPortal.arrangements.description')}
              control={control}
              variant="outlined"
            />
          </GridCompact6>

          <GridCompact6>
            <CompensationTypeIdFieldWithReadMode
              name="compensationTypeId"
              label={t('adminPortal.arrangements.typeOfArrangement')}
              control={control}
              options={convertArrangementTypeToOption(arrangementTypes ?? [])}
              readModeRenderer={(props) =>
                renderCompensationTypeIdReadMode({
                  ...props,
                  arrangementTypes: arrangementTypes
                })
              }
            />
          </GridCompact6>

          <GridCompact6>
            <TransportationModeFieldWithReadMode
              name="transportationMode"
              label={''}
              control={control}
              readModeRenderer={(props) =>
                renderTransportationModeFieldReadMode({
                  ...props,
                  transportationMode: Object.values(TransportationModeEnum)
                })
              }
            />
          </GridCompact6>
        </GridContainer>

        <Box display="flex" gap={2.5}>
          <GridCompact6>
            <IsExternalField control={control} disabled={!isEditable} />
          </GridCompact6>
        </Box>
      </Box>
      <TravelLinesTable rowData={rowData} closeInnerView={closeInnerView} />
    </StyledBox>
  );
};
